import {
  Box, Popper, styled,
} from '@mui/material';
import { lbAssays, type ISubmitAssayData } from '@zero-dash/types';
import {
  useLayoutEffect, useMemo, useRef, useState, type JSX,
} from 'react';
import { corePalette } from '@/themes/colours';
import CustomChip from '@/components/Common/Chip';
import { IBiomaterialRow } from '@/types/PatientDashboard/TimelineView.types';

const AssayChipsWrapper = styled(Box)({
  display: 'flex',
  gap: '8px',
  overflow: 'hidden',
  maxWidth: '100%',
  width: '100%',
  height: 24,
  flexWrap: 'wrap',
  position: 'relative',
});

interface IProps {
  row: Pick<IBiomaterialRow, 'name' | 'id' | 'assaysOrdered'>;
  dataToSubmit: ISubmitAssayData[];
}

export function AssaysOrderedCell({
  row,
  dataToSubmit,
}: IProps): JSX.Element {
  const assaysToSubmit = useMemo(() => (
    dataToSubmit
      .find((d) => d.biomaterialId === row.id)?.assays ?? []
  ), [dataToSubmit, row.id]);

  const assaysToShow = useMemo(() => (
    assaysToSubmit?.length
      ? assaysToSubmit
      : row.assaysOrdered ?? []
  ), [assaysToSubmit, row.assaysOrdered]);

  const [popperOpen, setPopperOpen] = useState<boolean>(false);
  const [visibleCount, setVisibleCount] = useState<number>(assaysToShow.length || 0);
  const chipWrapperRef = useRef<HTMLDivElement>(null);

  const renderAssayChip = (
    assay: string,
  ): JSX.Element => {
    const rowMatchesSelected = (): boolean => dataToSubmit.some((d) => d.biomaterialId === row.id);
    const label = (lbAssays[assay] ?? assay).toUpperCase();

    return (
      <CustomChip
        key={`${row.name}::${row.id}::${assay}`}
        label={label}
        colour={corePalette.grey200}
        backgroundColour={corePalette.grey30}
        sx={{
          background: rowMatchesSelected()
            ? `repeating-linear-gradient(45deg, ${corePalette.grey50}, ${corePalette.grey50} 3px, ${corePalette.grey30} 3px, ${corePalette.grey30} 6px)`
            : undefined,
        }}
      />
    );
  };

  useLayoutEffect(() => {
    const wrapper = chipWrapperRef.current;
    if (!wrapper) return undefined;

    const calculateVisibleChips = (): void => {
      const chips = Array.from(wrapper.children).filter(
        (el) => !el.hasAttribute('data-overflow-chip'),
      );
      const wrapperRight = wrapper.getBoundingClientRect().right;
      const wrapperBottom = wrapper.getBoundingClientRect().bottom;
      let howMany = 0;

      for (let i = 0; i < chips.length; i += 1) {
        const chipRight = chips[i].getBoundingClientRect().right;
        const chipBottom = chips[i].getBoundingClientRect().bottom;

        if (
          chipRight > wrapperRight
          || chipBottom > wrapperBottom
        ) {
          break;
        }
        howMany += 1;
      }
      setVisibleCount(howMany);
    };

    calculateVisibleChips();
    const observer = new ResizeObserver(calculateVisibleChips);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [assaysToShow]);

  const overflowCount = (assaysToShow?.length || 0) - visibleCount;

  return (
    <>
      <Box display="flex" gap="8px" alignItems="center" width="100%">
        <AssayChipsWrapper
          ref={chipWrapperRef}
          onMouseEnter={() => {
            if (overflowCount > 0) {
              setPopperOpen(true);
            }
          }}
          onMouseLeave={() => {
            setPopperOpen(false);
          }}
        >
          {assaysToShow.map((a) => renderAssayChip(a))}
        </AssayChipsWrapper>
        <Box width="40px">
          {overflowCount > 0 && (
            <CustomChip
              data-overflow-chip
              label={`+${overflowCount}`}
              size="medium"
              backgroundColour={corePalette.blank}
              pill={false}
              fontWeight="bold"
            />
          )}
        </Box>
      </Box>
      <Popper open={popperOpen} anchorEl={chipWrapperRef.current}>
        <Box
          bgcolor={corePalette.white}
          display="flex"
          gap="8px"
          padding="8px"
          flexWrap="wrap"
          boxShadow="0px 4px 12px rgba(0, 0, 0, 0.08)"
          sx={{
            width: 300,
            height: 'auto',
          }}
        >
          {assaysToShow.map((a) => renderAssayChip(a))}
        </Box>
      </Popper>
    </>
  );
}
